/**
 * The platform's opening price, and the short phrase a title and description carry.
 *
 * Search Console says the winnable intent on `/api/*` is the price question, not the
 * bare brand term: `glassdoor api pricing` ranks us 5th on Google today, while
 * `ahrefs api` sits at 56.7 behind four pages Ahrefs owns. The pages that beat us on
 * the price query put the figure in the title (`Reddit API Pricing 2026: From $0.002
 * per Call`); ours said only "per-request USD pricing" and never a number, while the
 * page body has carried the real one all along.
 *
 * `openingPrice` lives here rather than in `apiAnswer.ts` because the title, the
 * description, and the answer passage now all quote it, and a price with three
 * callers needs one owner.
 */
import type { ApiDetail } from '../../features/catalog/model';
import { cheapest } from '../catalog';
import type { DisplayPrice } from '../pricing';
import { m } from '../../paraglide/messages';
import { titleRate } from './answerParts';

/**
 * The cheapest endpoint's price across the platform.
 *
 * Endpoints are compared on per-request USD, the one denomination a flat endpoint and
 * a metered one share, and then quoted in that endpoint's own unit. Quoting a metered
 * endpoint per request would print `$110.00 per 1,000 requests` beside the header's
 * `$5.50 /1k results` and be a second, worse source of truth for the same money.
 */
export const openingPrice = (detail: ApiDetail): DisplayPrice | undefined => {
  const prices = detail.endpoints
    .filter((endpoint) => endpoint.providers.length > 0)
    .map((endpoint) => cheapest(endpoint).price);
  if (prices.length === 0) return undefined;
  return prices.reduce((low, p) => (p.perRequestUsd < low.perRequestUsd ? p : low));
};

/**
 * `jobs from $5.50 per 1,000 results`, or null when nothing on the platform is priced.
 *
 * The action is named because the header prints `From - <action> $X` for the platform's
 * most-requested endpoint, and the cheapest is often a different one. Instagram's
 * cheapest is $0.90 while its busiest quotes $1.50: two true figures that read as a
 * contradiction unless each says which endpoint it belongs to.
 */
export const platformPricePhrase = (detail: ApiDetail): string | null => {
  const priced = detail.endpoints.filter((endpoint) => endpoint.providers.length > 0);
  if (priced.length === 0) return null;
  const lead = priced.reduce((low, endpoint) =>
    cheapest(endpoint).price.perRequestUsd < cheapest(low).price.perRequestUsd ? endpoint : low,
  );
  return m.seo_price_phrase({ action: lead.action.trim().toLowerCase(), rate: titleRate(cheapest(lead).price) });
};
